import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Menu, X, Play, Pause, Home, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import { CoverPreview } from "./CoverPreview";
import type { MagazineTitle, MagazineIssue, MagazinePage, MagazineObject, Article, MagazineTemplate } from "@shared/schema";

interface AdCreativeData {
  id: string;
  label: string;
  imageUrl: string | null;
  clickThroughUrl: string | null;
  format: string;
  adSize: string;
}

interface PageWithObjects extends MagazinePage {
  objects: (MagazineObject & { article?: Article | null; adCreative?: AdCreativeData | null })[];
  template?: MagazineTemplate | null;
  heroArticle?: Article | null;
  mainHeadlineArticle?: Article | null;
  secondaryHeadlineArticles?: Article[];
}

interface MagazineViewerProps {
  title: MagazineTitle;
  issue: MagazineIssue;
  pages: PageWithObjects[];
  className?: string;
}

// Types for spread-based navigation
type SpreadItem = 
  | { type: 'sponsor-left'; position: 'cover' | 'back-cover' }
  | { type: 'sponsor-right'; position: 'cover' | 'back-cover' }
  | { type: 'page'; page: PageWithObjects };

type Spread = {
  id: string;
  left: SpreadItem | null;
  right: SpreadItem | null;
};

function buildSpreads(pages: PageWithObjects[], isSpread: boolean): Spread[] {
  if (!isSpread) {
    // Mobile: single pages, no sponsors
    return pages.map((page, idx) => ({
      id: `mobile-${idx}`,
      left: null,
      right: { type: 'page', page },
    }));
  }

  const spreads: Spread[] = [];
  
  // Find cover and back cover
  const coverPage = pages.find(p => p.pageType === 'front_cover');
  const backCoverPage = pages.find(p => p.pageType === 'back_cover');
  const middlePages = pages.filter(p => p.pageType !== 'front_cover' && p.pageType !== 'back_cover');
  
  // First spread: Sponsor (left) + Cover (right)
  if (coverPage) {
    spreads.push({
      id: 'cover-spread',
      left: { type: 'sponsor-left', position: 'cover' },
      right: { type: 'page', page: coverPage },
    });
  }
  
  // Middle spreads: pairs of pages
  for (let i = 0; i < middlePages.length; i += 2) {
    const leftPage = middlePages[i];
    const rightPage = middlePages[i + 1];
    
    spreads.push({
      id: `spread-${i}`,
      left: leftPage ? { type: 'page', page: leftPage } : null,
      right: rightPage ? { type: 'page', page: rightPage } : null,
    });
  }
  
  // Last spread: Back Cover (left) + Sponsor (right)
  if (backCoverPage) {
    // If the last middle spread has an odd page, we need to adjust
    const lastSpread = spreads[spreads.length - 1];
    if (lastSpread && lastSpread.id !== 'cover-spread' && lastSpread.right === null) {
      // Move back cover to be right of the last middle page
      lastSpread.right = { type: 'page', page: backCoverPage };
      // Add final sponsor spread
      spreads.push({
        id: 'back-cover-spread',
        left: null,
        right: { type: 'sponsor-right', position: 'back-cover' },
      });
    } else {
      spreads.push({
        id: 'back-cover-spread',
        left: { type: 'page', page: backCoverPage },
        right: { type: 'sponsor-right', position: 'back-cover' },
      });
    }
  }
  
  return spreads;
}

export function MagazineViewer({ title, issue, pages, className }: MagazineViewerProps) {
  const [currentSpreadIndex, setCurrentSpreadIndex] = useState(0);
  const [isSpread, setIsSpread] = useState(false);
  const [showToc, setShowToc] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const spreadRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Build spreads based on current mode
  const spreads = buildSpreads(pages, isSpread);

  useEffect(() => {
    const checkWidth = () => {
      const width = window.innerWidth;
      setIsSpread(width >= 1024);
    };
    
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  useEffect(() => {
    const firstSlide = spreadRefs.current[0];
    if (firstSlide && scrollerRef.current) {
      setTimeout(() => {
        scrollerRef.current?.scrollTo({ left: 0, behavior: "auto" });
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!scrollerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = spreadRefs.current.findIndex(el => el === entry.target);
            if (index !== -1) {
              setCurrentSpreadIndex(index);
            }
          }
        }
      },
      {
        root: scrollerRef.current,
        threshold: 0.5,
      }
    );

    spreadRefs.current.forEach((slide) => {
      if (slide) observer.observe(slide);
    });

    return () => observer.disconnect();
  }, [spreads.length]);

  useEffect(() => {
    const slide = spreadRefs.current[currentSpreadIndex];
    if (slide) {
      slide.scrollIntoView({ behavior: "auto", inline: "start" });
    }
  }, [isSpread]);

  const goToSpread = useCallback((index: number) => {
    const slide = spreadRefs.current[index];
    if (slide) {
      slide.scrollIntoView({ behavior: "smooth", inline: "start" });
    }
  }, []);

  // Map page index to spread index for TOC navigation
  const goToPage = useCallback((pageIndex: number) => {
    const targetPage = pages[pageIndex];
    if (!targetPage) return;
    
    // Find which spread contains this page
    const spreadIndex = spreads.findIndex(spread => {
      const leftPage = spread.left?.type === 'page' ? spread.left.page : null;
      const rightPage = spread.right?.type === 'page' ? spread.right.page : null;
      return leftPage?.id === targetPage.id || rightPage?.id === targetPage.id;
    });
    
    if (spreadIndex !== -1) {
      goToSpread(spreadIndex);
    }
  }, [pages, spreads, goToSpread]);

  const nextSpread = useCallback(() => {
    const nextIndex = Math.min(currentSpreadIndex + 1, spreads.length - 1);
    goToSpread(nextIndex);
  }, [currentSpreadIndex, spreads.length, goToSpread]);

  const prevSpread = useCallback(() => {
    const prevIndex = Math.max(currentSpreadIndex - 1, 0);
    goToSpread(prevIndex);
  }, [currentSpreadIndex, goToSpread]);

  const canGoPrev = currentSpreadIndex > 0;
  const canGoNext = currentSpreadIndex < spreads.length - 1;
  
  // Calculate current page for progress bar
  const getCurrentPageIndex = () => {
    const currentSpread = spreads[currentSpreadIndex];
    if (!currentSpread) return 0;
    
    // Get the first page in this spread
    const leftPage = currentSpread.left?.type === 'page' ? currentSpread.left.page : null;
    const rightPage = currentSpread.right?.type === 'page' ? currentSpread.right.page : null;
    const firstPage = leftPage || rightPage;
    
    if (firstPage) {
      return pages.findIndex(p => p.id === firstPage.id);
    }
    return 0;
  };
  
  const currentPageIndex = getCurrentPageIndex();

  return (
    <div className={cn("relative h-screen flex flex-col bg-neutral-100 dark:bg-neutral-900 overflow-hidden", className)}>
      {/* Minimal fixed header with breadcrumbs and magazine masthead */}
      <header className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Breadcrumbs */}
          <nav 
            className="flex items-center gap-1 bg-white/90 dark:bg-neutral-800/90 rounded-md px-3 py-1.5 shadow-sm text-xs"
            aria-label="Breadcrumb"
            data-testid="nav-breadcrumbs"
          >
            <Link 
              href="/"
              className="text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1" 
              data-testid="link-home"
            >
              <Home className="w-3.5 h-3.5" />
              <span className="sr-only sm:not-sr-only">Home</span>
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
            <Link 
              href="/magazine"
              className="text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors" 
              data-testid="link-magazines"
            >
              Magazine
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
            <span className="text-neutral-700 dark:text-neutral-300 font-medium truncate max-w-[120px]" data-testid="text-current-magazine">
              {issue.name ? issue.name : `${title.name} Issue ${issue.issueNumber}`}
            </span>
          </nav>
          
          {/* TOC Toggle */}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setShowToc(!showToc)}
            className="bg-white/90 dark:bg-neutral-800/90 shadow-sm hover:bg-white dark:hover:bg-neutral-800"
            data-testid="button-toggle-toc"
          >
            {showToc ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      {/* Table of Contents Overlay */}
      {showToc && (
        <div 
          className="absolute inset-0 z-40 bg-black/50"
          onClick={() => setShowToc(false)}
        >
          <div 
            className="absolute top-0 left-0 bottom-0 w-80 bg-white dark:bg-neutral-900 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl font-bold">Contents</h2>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowToc(false)}
                  data-testid="button-close-toc"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="text-xs text-muted-foreground mb-4 uppercase tracking-wider">
                Issue {issue.issueNumber} {issue.name && `• ${issue.name}`}
              </div>
              
              <ul className="space-y-2">
                {pages.map((page, idx) => {
                  const article = page.objects.find(o => o.article)?.article;
                  // Check if this page is in the current spread
                  const currentSpread = spreads[currentSpreadIndex];
                  const leftPage = currentSpread?.left?.type === 'page' ? currentSpread.left.page : null;
                  const rightPage = currentSpread?.right?.type === 'page' ? currentSpread.right.page : null;
                  const isActive = leftPage?.id === page.id || rightPage?.id === page.id;
                  
                  return (
                    <li key={page.id}>
                      <button
                        onClick={() => {
                          goToPage(idx);
                          setShowToc(false);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-3 rounded-lg transition-colors flex items-start gap-3",
                          isActive 
                            ? "bg-primary/10 text-primary" 
                            : "hover:bg-muted"
                        )}
                        data-testid={`button-goto-page-${idx + 1}`}
                      >
                        <span className="text-xs font-medium text-muted-foreground w-6">
                          {String(page.pageNumber).padStart(2, "0")}
                        </span>
                        <div className="flex-1 min-w-0">
                          {article ? (
                            <>
                              <div className="font-medium text-sm line-clamp-2">
                                {article.title}
                              </div>
                              {article.excerpt && (
                                <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                  {article.excerpt.slice(0, 60)}...
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              {getPageTypeLabel(page.pageType)}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Main content scroller - centered with max width */}
      <div className="flex-1 flex items-center justify-center pt-14 pb-2 px-16 overflow-hidden">
        <div 
          ref={scrollerRef}
          className="h-full overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory no-scrollbar"
          style={{ 
            WebkitOverflowScrolling: "touch",
            scrollBehavior: "auto",
            maxWidth: isSpread ? "1500px" : "750px",
            width: "100%"
          }}
          data-testid="magazine-scroller"
        >
          {spreads.map((spread, idx) => (
            <div
              key={spread.id}
              ref={(el) => { spreadRefs.current[idx] = el; }}
              className={cn(
                "flex-none h-full snap-start overflow-hidden flex",
                isSpread ? "w-full" : "w-full"
              )}
              style={{ scrollSnapStop: "always" }}
              data-testid={`spread-${idx}`}
            >
              {/* Left side of spread */}
              <div className={cn("h-full", isSpread ? "w-1/2" : "hidden")}>
                {spread.left?.type === 'sponsor-left' && (
                  <SponsorPlaceholder position={spread.left.position} side="left" />
                )}
                {spread.left?.type === 'sponsor-right' && (
                  <SponsorPlaceholder position={spread.left.position} side="right" />
                )}
                {spread.left?.type === 'page' && (
                  <MagazinePageView 
                    page={spread.left.page} 
                    title={title}
                    issue={issue}
                    isSpread={isSpread}
                    totalPages={pages.length}
                    isLeftPage={true}
                  />
                )}
                {!spread.left && <div className="h-full" />}
              </div>
              
              {/* Right side of spread */}
              <div className={cn("h-full", isSpread ? "w-1/2" : "w-full")}>
                {spread.right?.type === 'sponsor-left' && (
                  <SponsorPlaceholder position={spread.right.position} side="left" />
                )}
                {spread.right?.type === 'sponsor-right' && (
                  <SponsorPlaceholder position={spread.right.position} side="right" />
                )}
                {spread.right?.type === 'page' && (
                  <MagazinePageView 
                    page={spread.right.page} 
                    title={title}
                    issue={issue}
                    isSpread={isSpread}
                    totalPages={pages.length}
                    isLeftPage={false}
                  />
                )}
                {!spread.right && <div className="h-full" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prevSpread}
        disabled={!canGoPrev}
        className={cn(
          "absolute left-4 top-1/2 -translate-y-1/2 w-10 h-20 flex items-center justify-center",
          "text-neutral-400 hover:text-neutral-600 dark:text-neutral-600 dark:hover:text-neutral-400",
          "transition-colors disabled:opacity-0"
        )}
        data-testid="button-prev-page"
      >
        <ChevronLeft className="w-8 h-8" />
      </button>
      
      <button
        onClick={nextSpread}
        disabled={!canGoNext}
        className={cn(
          "absolute right-4 top-1/2 -translate-y-1/2 w-10 h-20 flex items-center justify-center",
          "text-neutral-400 hover:text-neutral-600 dark:text-neutral-600 dark:hover:text-neutral-400",
          "transition-colors disabled:opacity-0"
        )}
        data-testid="button-next-page"
      >
        <ChevronRight className="w-8 h-8" />
      </button>

      {/* Subtle progress indicator */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-200 dark:bg-neutral-800">
        <div 
          className="h-full bg-neutral-400 dark:bg-neutral-600 transition-all duration-300"
          style={{ width: `${((currentPageIndex + 1) / pages.length) * 100}%` }}
          data-testid="progress-bar"
        />
      </div>
    </div>
  );
}

function getPageTypeLabel(pageType: string | null | undefined): string {
  switch (pageType) {
    case "front_cover": return "Cover";
    case "inside_front": return "Inside Front";
    case "contents": return "Contents";
    case "leader": return "Editor's Note";
    case "inside_back": return "Inside Back";
    case "back_cover": return "Back Cover";
    default: return "Page";
  }
}

// Sponsor placeholder component - half size, centered
function SponsorPlaceholder({ position, side }: { position: 'cover' | 'back-cover'; side: 'left' | 'right' }) {
  return (
    <div 
      className="h-full w-full flex items-center justify-center bg-neutral-50 dark:bg-neutral-950"
      data-testid={`sponsor-placeholder-${position}-${side}`}
    >
      <div 
        className="w-1/2 aspect-[3/4] max-h-[50%] flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900"
      >
        <div className="text-center p-6">
          <div className="text-[10px] font-mono tracking-widest text-muted-foreground uppercase mb-3">
            Sponsorship Opportunity
          </div>
          <div className="text-xl font-serif font-bold text-neutral-400 dark:text-neutral-600 uppercase">
            {position === 'cover' ? 'Front Cover' : 'Back Cover'}
          </div>
          <div className="text-xs font-mono text-muted-foreground mt-2">
            Partner with us
          </div>
        </div>
      </div>
    </div>
  );
}

interface MagazinePageViewProps {
  page: PageWithObjects;
  title: MagazineTitle;
  issue: MagazineIssue;
  isSpread: boolean;
  totalPages: number;
  isLeftPage: boolean;
}

function MagazinePageView({ page, title, issue, isSpread, totalPages, isLeftPage }: MagazinePageViewProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentWordRange, setCurrentWordRange] = useState<WordRange | null>(null);
  const isCover = page.pageType === "front_cover";
  const isBackCover = page.pageType === "back_cover";
  
  // Get article text for both audio and highlighting
  const article = page.objects.find(o => o.article)?.article;
  const articleText = article ? getArticleFullText(article) : "";
  
  if (isCover) {
    return <CoverPage page={page} title={title} issue={issue} />;
  }
  
  if (isBackCover) {
    return <BackCoverPage page={page} title={title} issue={issue} />;
  }
  
  return (
    <div className="h-full flex flex-col shadow-lg overflow-hidden" style={{ backgroundColor: '#F5F4F3' }}>
      {/* Page Furniture - Header with progress line */}
      <PageFurnitureHeader 
        page={page} 
        title={title} 
        issue={issue} 
        totalPages={totalPages}
        isLeftPage={isLeftPage}
        scrollProgress={scrollProgress}
        articleText={articleText}
        onWordHighlight={setCurrentWordRange}
      />
      
      {/* Page Content - scrollable container with scroll tracking */}
      <ScrollablePageContent 
        page={page} 
        title={title} 
        issue={issue} 
        onScrollProgress={setScrollProgress}
        currentWordRange={currentWordRange}
        articleText={articleText}
      />
    </div>
  );
}

interface PageFurnitureHeaderProps {
  page: PageWithObjects;
  title: MagazineTitle;
  issue: MagazineIssue;
  totalPages: number;
  isLeftPage: boolean;
  scrollProgress: number;
  articleText: string;
  onWordHighlight: (range: WordRange | null) => void;
}

function PageFurnitureHeader({ page, title, issue, totalPages, isLeftPage, scrollProgress, articleText, onWordHighlight }: PageFurnitureHeaderProps) {
  const article = page.objects.find(o => o.article)?.article;
  const sectionName = getSectionName(page, article);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  
  const hasContent = articleText.length > 0;
  
  return (
    <div className="px-6 md:px-12 pt-6 md:pt-12 shrink-0 z-20" style={{ backgroundColor: '#F5F4F3' }}>
      {/* Header bar - monospace, 10px, wide tracking */}
      <div className="w-full flex justify-between items-center pb-2 text-[10px] font-mono tracking-widest text-muted-foreground uppercase">
        {/* Left side cluster */}
        <div className="flex items-center gap-4">
          {/* Brand block */}
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-accent" />
            <span>{title.name} // {sectionName}</span>
          </div>
          
          {/* Audio controls - separated by vertical rule, only show if there's content */}
          {hasContent && (
            <div className="pl-4 border-l border-foreground/10">
              <button 
                onClick={() => setShowAudioPlayer(!showAudioPlayer)}
                className="flex items-center gap-1 hover:text-accent transition-colors"
                data-testid="button-listen"
              >
                <Play className="w-3 h-3" />
                <span className="hidden sm:inline">Listen</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Right side - issue/page counter */}
        <div>
          Issue {issue.issueNumber} // PG {String(page.pageNumber).padStart(2, "0")}
        </div>
      </div>
      
      {/* Dual ruler system */}
      <div className="mb-6" data-testid="dual-ruler-container">
        {/* Navigation ruler - thin static line */}
        <div className="h-[1px] bg-neutral-400 dark:bg-neutral-500" data-testid="ruler-navigation" />
        
        {/* Reading progress ruler - second line below with fill indicator */}
        <div className="relative h-[3px] mt-1" data-testid="ruler-progress-container">
          {/* Track (background) */}
          <div className="absolute inset-0 h-[1px] top-[1px] bg-neutral-300 dark:bg-neutral-600" />
          {/* Fill (reading progress indicator) */}
          <div 
            className="absolute left-0 top-0 h-[3px] bg-neutral-800 dark:bg-neutral-200 transition-all duration-150 ease-out"
            style={{ width: `${scrollProgress}%` }}
            data-testid="ruler-progress-fill"
          />
        </div>
      </div>
      
      {/* Audio Player - slides down when Listen is clicked */}
      {showAudioPlayer && hasContent && (
        <AudioPlayerBar 
          text={articleText}
          onClose={() => setShowAudioPlayer(false)}
          onWordHighlight={onWordHighlight}
        />
      )}
    </div>
  );
}

// Word highlight range for speech synthesis
interface WordRange {
  start: number;
  end: number;
}

// Audio player component using Web Speech API
interface AudioPlayerBarProps {
  onClose: () => void;
  text: string;
  onWordHighlight?: (range: WordRange | null) => void;
}

function AudioPlayerBar({ onClose, text, onWordHighlight }: AudioPlayerBarProps) {
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      onWordHighlight?.(null);
    };
  }, [onWordHighlight]);
  
  const handlePlay = useCallback(() => {
    if (!window.speechSynthesis) {
      console.warn("Speech synthesis not supported");
      return;
    }
    
    if (paused) {
      // Resume paused speech
      window.speechSynthesis.resume();
      setPaused(false);
      setSpeaking(true);
      return;
    }
    
    // Cancel any previous utterance
    window.speechSynthesis.cancel();
    
    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    
    // Set up event handlers
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        const start = event.charIndex;
        const end = start + (event.charLength || 0);
        onWordHighlight?.({ start, end });
      }
    };
    
    utterance.onend = () => {
      setSpeaking(false);
      setPaused(false);
      onWordHighlight?.(null);
    };
    
    utterance.onerror = () => {
      setSpeaking(false);
      setPaused(false);
      onWordHighlight?.(null);
    };
    
    // Start speaking
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
    setPaused(false);
  }, [text, paused, onWordHighlight]);
  
  const handlePause = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.pause();
      setPaused(true);
      setSpeaking(false);
    }
  }, []);
  
  const handleStop = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
    setPaused(false);
    onWordHighlight?.(null);
  }, [onWordHighlight]);
  
  const handleClose = useCallback(() => {
    handleStop();
    onClose();
  }, [handleStop, onClose]);
  
  const isActive = speaking || paused;
  
  return (
    <div className="px-6 md:px-12 py-3 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 mb-4">
      <div className="flex items-center gap-4">
        {/* Play/Pause button */}
        <button
          onClick={speaking ? handlePause : handlePlay}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-accent text-white hover:bg-accent/90 transition-colors"
          data-testid="button-audio-play"
          aria-label={speaking ? "Pause audio" : (paused ? "Resume audio" : "Play audio")}
        >
          {speaking ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        
        {/* Status text */}
        <div className="flex-1 text-xs text-muted-foreground font-mono uppercase tracking-wider">
          {speaking ? "Speaking..." : paused ? "Paused" : "Ready to listen"}
        </div>
        
        {/* Stop button - only show when active */}
        {isActive && (
          <button
            onClick={handleStop}
            className="text-muted-foreground hover:text-foreground transition-colors text-xs font-mono uppercase tracking-wider"
            data-testid="button-audio-stop"
            aria-label="Stop audio"
          >
            Stop
          </button>
        )}
        
        {/* Close */}
        <button
          onClick={handleClose}
          className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          data-testid="button-close-audio"
          aria-label="Close audio player"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function getSectionName(page: PageWithObjects, article?: Article | null): string {
  if (page.pageType === "contents") return "Contents";
  if (page.pageType === "leader") return "Editor's Note";
  if (page.pageType === "inside_front" || page.pageType === "inside_back") {
    if (article) {
      if (article.contentType === "video") return "Video";
      if (article.technologyType) return article.technologyType;
      return "Feature";
    }
    return "Advertisement";
  }
  
  // Get section from article content type
  if (article?.contentType === "video") return "Video";
  if (article?.technologyType) return article.technologyType;
  
  return "Feature";
}

// Extract full text from article for audio playback (content only, no title/excerpt)
function getArticleFullText(article: Article): string {
  if (!article.content) return "";
  
  if (typeof article.content === 'string') {
    // HTML content - strip tags and decode entities
    return article.content
      .replace(/<[^>]*>/g, ' ')  // Remove HTML tags
      .replace(/&nbsp;/g, ' ')   // Replace nbsp
      .replace(/&amp;/g, '&')    // Replace amp
      .replace(/&lt;/g, '<')     // Replace lt
      .replace(/&gt;/g, '>')     // Replace gt
      .replace(/&quot;/g, '"')   // Replace quot
      .replace(/&#39;/g, "'")    // Replace apos
      .replace(/\s+/g, ' ')      // Normalize whitespace
      .trim();
  }
  
  if (typeof article.content === 'object') {
    // Structured content (Tiptap/ProseMirror format)
    const content = article.content as { content?: Array<{ type?: string; content?: Array<{ text?: string }> }> };
    if (content.content && Array.isArray(content.content)) {
      const parts: string[] = [];
      for (const node of content.content) {
        if (node.content && Array.isArray(node.content)) {
          const text = node.content
            .map(child => child.text || '')
            .join('');
          if (text.trim()) {
            parts.push(text);
          }
        }
      }
      return parts.join(' ');
    }
  }
  
  return "";
}

// Scrollable page content wrapper with progress tracking
interface ScrollablePageContentProps {
  page: PageWithObjects;
  title: MagazineTitle;
  issue: MagazineIssue;
  onScrollProgress: (progress: number) => void;
  currentWordRange: WordRange | null;
  articleText: string;
}

function ScrollablePageContent({ page, title, issue, onScrollProgress, currentWordRange, articleText }: ScrollablePageContentProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const maxScroll = scrollHeight - clientHeight;
    // If there's nothing to scroll (content fits), show 100% progress
    // Otherwise calculate the percentage scrolled
    const progress = maxScroll <= 0 ? 100 : (scrollTop / maxScroll) * 100;
    onScrollProgress(Math.min(100, Math.max(0, progress)));
  }, [onScrollProgress]);
  
  // Reset scroll position and recalculate progress when page changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    // Recalculate progress after reset
    setTimeout(handleScroll, 0);
  }, [page.id, handleScroll]);
  
  return (
    <div 
      ref={scrollRef}
      className="flex-1 overflow-y-auto no-scrollbar" 
      data-testid="article-scroll-container"
      onScroll={handleScroll}
    >
      <PageContent page={page} title={title} issue={issue} currentWordRange={currentWordRange} articleText={articleText} />
    </div>
  );
}

interface PageContentProps {
  page: PageWithObjects;
  title: MagazineTitle;
  issue: MagazineIssue;
  currentWordRange: WordRange | null;
  articleText: string;
}

function PageContent({ page, title, issue, currentWordRange, articleText }: PageContentProps) {
  const article = page.objects.find(o => o.article)?.article;
  
  if (page.pageType === "contents") {
    return <ContentsPageContent title={title} issue={issue} />;
  }
  
  if (page.pageType === "leader") {
    return <LeaderPageContent title={title} issue={issue} />;
  }
  
  if (page.pageType === "inside_front" || page.pageType === "inside_back") {
    if (article) {
      return <ArticlePageContent article={article} currentWordRange={currentWordRange} articleText={articleText} />;
    }
    const adCreative = page.objects.find(o => o.adCreative?.imageUrl)?.adCreative ?? null;
    return <AdvertisementPage adCreative={adCreative} />;
  }
  
  if (!article) {
    return <EmptyPageContent />;
  }
  
  return <ArticlePageContent article={article} currentWordRange={currentWordRange} articleText={articleText} />;
}

function CoverPage({ page, title, issue }: { page: PageWithObjects; title: MagazineTitle; issue: MagazineIssue }) {
  // If page has a template, use CoverPreview to render it
  if (page.template) {
    // Get linked article data
    const heroArticle = page.heroArticle;
    const mainHeadlineArticle = page.mainHeadlineArticle;
    const secondaryHeadlineArticles = page.secondaryHeadlineArticles || [];
    
    // Build cover data from linked articles
    const coverImage = heroArticle?.featuredImage;
    const mainArticle = mainHeadlineArticle ? {
      title: mainHeadlineArticle.title,
      subtitle: mainHeadlineArticle.metaTitle || mainHeadlineArticle.excerpt,
      author: null, // Could get from article author if available
    } : null;
    const secondaryArticles = secondaryHeadlineArticles.map(article => ({
      title: article.title,
      pageNumber: undefined,
    }));
    
    return (
      <div className="h-full w-full">
        <CoverPreview
          template={page.template}
          title={title}
          issue={issue}
          coverImage={coverImage}
          mainArticle={mainArticle}
          secondaryArticles={secondaryArticles}
          className="h-full w-full"
        />
      </div>
    );
  }
  
  // Fallback: use legacy cover rendering if no template
  const heroArticle = page.objects.find(o => o.article)?.article;
  const heroImage = heroArticle?.featuredImage;
  
  return (
    <div className="h-full relative overflow-hidden bg-neutral-900">
      {/* Background image with gradient overlay */}
      {heroImage ? (
        <>
          <img 
            src={heroImage} 
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-900" />
      )}
      
      {/* Cover content */}
      <div className="relative h-full flex flex-col p-8">
        {/* Top section with date - IBM Plex Mono */}
        <div className="flex items-center gap-2 text-white/60 text-[10px] font-mono uppercase tracking-widest">
          <span>{issue.publishedAt ? new Date(issue.publishedAt).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}</span>
        </div>
        
        {/* Masthead - Space Grotesk */}
        <div className="mt-auto mb-8">
          <h1 className="font-serif text-[15vw] md:text-6xl lg:text-8xl font-bold text-white leading-none tracking-tight uppercase">
            {title.name?.split("").slice(0, 4).join("")}
          </h1>
          {title.tagline && (
            <p className="mt-4 text-white/80 text-sm md:text-base font-mono uppercase tracking-[0.3em]">
              {title.tagline}
            </p>
          )}
        </div>
        
        {/* Issue info - IBM Plex Mono */}
        <div className="text-white/50 text-[10px] font-mono uppercase tracking-widest">
          CVR
        </div>
      </div>
    </div>
  );
}

function BackCoverPage({ page, title, issue }: { page: PageWithObjects; title: MagazineTitle; issue: MagazineIssue }) {
  return (
    <div className="h-full relative overflow-hidden bg-neutral-900 flex items-center justify-center">
      <div className="text-center">
        <div className="font-serif text-4xl font-bold text-white mb-4 uppercase">
          {title.name}
        </div>
        {title.tagline && (
          <p className="text-white/60 text-sm font-mono uppercase tracking-widest">
            {title.tagline}
          </p>
        )}
        <div className="mt-8 text-white/40 text-[10px] font-mono uppercase tracking-widest">
          Issue {issue.issueNumber}
        </div>
      </div>
    </div>
  );
}

function ContentsPageContent({ title, issue }: { title: MagazineTitle; issue: MagazineIssue }) {
  return (
    <div className="p-8 md:p-12">
      <div className="mb-8">
        <span className="text-[10px] font-mono text-red-600 uppercase tracking-widest font-medium">
          In This Issue
        </span>
        <h2 className="font-serif text-4xl md:text-5xl font-bold mt-2 uppercase">
          Contents
        </h2>
      </div>
      <p className="text-neutral-500 font-body italic">
        Contents will be auto-generated from issue articles
      </p>
    </div>
  );
}

function LeaderPageContent({ title, issue }: { title: MagazineTitle; issue: MagazineIssue }) {
  return (
    <div className="p-8 md:p-12">
      <div className="mb-8">
        <span className="text-[10px] font-mono text-red-600 uppercase tracking-widest font-medium">
          From the Editor
        </span>
        <h2 className="font-serif text-4xl md:text-5xl font-bold mt-2 uppercase">
          Editor's Note
        </h2>
      </div>
      <p className="text-neutral-500 font-body italic">
        Editor's note content will be customizable
      </p>
    </div>
  );
}

function AdvertisementPage({ adCreative }: { adCreative?: AdCreativeData | null }) {
  if (adCreative?.imageUrl) {
    const content = (
      <div className="h-full w-full relative overflow-hidden bg-black">
        <img
          src={adCreative.imageUrl}
          alt={adCreative.label}
          className="w-full h-full object-contain"
        />
        <span className="absolute bottom-1 right-2 text-[8px] font-mono text-white/40 uppercase tracking-widest">Advertisement</span>
      </div>
    );
    if (adCreative.clickThroughUrl) {
      return (
        <a href={adCreative.clickThroughUrl} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
          {content}
        </a>
      );
    }
    return content;
  }

  return (
    <div className="h-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
      <div className="text-center">
        <span className="text-[10px] font-mono text-neutral-400 uppercase tracking-widest">
          Advertisement
        </span>
      </div>
    </div>
  );
}

function EmptyPageContent() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center text-neutral-400">
        <p className="text-sm">No content assigned</p>
      </div>
    </div>
  );
}

// Extract insight links from article content
function InsightLinksSection({ content }: { content: string }) {
  // Extract all links from the HTML content
  const linkRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;
  const links: { url: string; text: string }[] = [];
  let match;
  
  while ((match = linkRegex.exec(content)) !== null) {
    // Filter for external links or insight links
    if (match[1].startsWith("http") || match[1].includes("insight") || match[1].includes("glossary")) {
      links.push({ url: match[1], text: match[2] });
    }
  }
  
  if (links.length === 0) return null;
  
  return (
    <div className="mt-12 pt-8 border-t border-neutral-200 dark:border-neutral-700">
      <h3 className="text-sm uppercase tracking-[0.15em] text-neutral-500 mb-4 font-medium">
        Related Insights
      </h3>
      <div className="space-y-3">
        {links.slice(0, 5).map((link, idx) => (
          <a 
            key={idx}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-red-600 hover:underline text-sm"
            data-testid={`link-insight-${idx}`}
          >
            {link.text}
          </a>
        ))}
      </div>
    </div>
  );
}

interface ArticlePageContentProps {
  article: Article;
  currentWordRange: WordRange | null;
  articleText: string;
}

function ArticlePageContent({ article, currentWordRange, articleText }: ArticlePageContentProps) {
  const wordCount = article.content ? article.content.replace(/<[^>]*>/g, "").split(/\s+/).filter(w => w.length > 0).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));
  
  // Extract first paragraph for drop cap
  const content = article.content || "";
  const firstParagraphMatch = content.match(/<p[^>]*>([\s\S]*?)<\/p>/);
  const firstParagraph = firstParagraphMatch ? firstParagraphMatch[1].replace(/<[^>]*>/g, "") : "";
  const restContent = firstParagraphMatch 
    ? content.replace(firstParagraphMatch[0], "") 
    : content;
  
  // Get first letter for drop cap
  const firstLetter = firstParagraph.trim().charAt(0).toUpperCase();
  const restOfFirstParagraph = firstParagraph.trim().slice(1);
  
  // Render text with word highlighting for audio playback
  // Uses occurrence counting to find the correct instance of a word
  const renderHighlightedText = (text: string, textOffsetInArticle: number) => {
    if (!currentWordRange || !articleText) {
      return text;
    }
    
    // Get the current word being spoken
    const highlightedWord = articleText.slice(currentWordRange.start, currentWordRange.end);
    
    // Check if this word is within this text segment's range
    // The first paragraph in content starts after offset 0, so check if we're in range
    const textEndOffset = textOffsetInArticle + text.length;
    
    // Only highlight if the current word position falls within this text segment
    if (currentWordRange.start < textOffsetInArticle || currentWordRange.start > textEndOffset) {
      return text;
    }
    
    // Calculate local position within this text
    const localStart = currentWordRange.start - textOffsetInArticle;
    const localEnd = currentWordRange.end - textOffsetInArticle;
    
    // Find the word at this position
    const wordPattern = new RegExp(`\\b${highlightedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    let match;
    let foundMatch = null;
    
    while ((match = wordPattern.exec(text)) !== null) {
      // Check if this match is close to our expected position
      if (Math.abs(match.index - localStart) < 5) {
        foundMatch = match;
        break;
      }
    }
    
    if (!foundMatch) {
      // Fallback: find by occurrence count
      const textBeforeWord = articleText.slice(0, currentWordRange.start);
      const matchesBefore = (textBeforeWord.match(wordPattern) || []).length;
      
      let occurrenceCount = 0;
      wordPattern.lastIndex = 0;
      while ((match = wordPattern.exec(text)) !== null) {
        if (occurrenceCount === matchesBefore % 10) { // Rough heuristic
          foundMatch = match;
          break;
        }
        occurrenceCount++;
      }
    }
    
    if (!foundMatch) {
      return text;
    }
    
    const before = text.slice(0, foundMatch.index);
    const word = text.slice(foundMatch.index, foundMatch.index + highlightedWord.length);
    const after = text.slice(foundMatch.index + highlightedWord.length);
    
    return (
      <>
        {before}
        <span className="bg-yellow-300 dark:bg-yellow-500/80 text-neutral-900 px-1 py-0.5 rounded transition-colors" data-testid="highlighted-word">
          {word}
        </span>
        {after}
      </>
    );
  };
  
  return (
    <div className="px-6 md:px-12">
        {/* Section label badge - IBM Plex Mono */}
        {article.technologyType && (
          <div className="mb-6">
            <span className="inline-block px-3 py-1 border border-neutral-300 dark:border-neutral-600 text-[10px] font-mono uppercase tracking-widest text-neutral-600 dark:text-neutral-400 font-medium">
              {article.technologyType}
            </span>
          </div>
        )}
        
        {/* Headline - Space Grotesk, bold uppercase */}
        <h1 
          className="font-serif text-4xl md:text-6xl font-bold uppercase leading-none mb-6 tracking-tight"
          data-testid="text-article-title"
        >
          {article.title}
        </h1>
        
        {/* Meta info - IBM Plex Mono */}
        <div className="flex items-center gap-3 text-[10px] font-mono text-neutral-400 uppercase tracking-widest mb-10">
          <span>Read Time: {readTime} Min</span>
          <span className="text-neutral-300 dark:text-neutral-600">//</span>
          <span>Words: {wordCount.toLocaleString()}</span>
        </div>
        
        {/* Featured image */}
        {article.featuredImage && (
          <figure className="mb-10 -mx-2 md:-mx-4">
            <img 
              src={article.featuredImage} 
              alt={article.title || ""} 
              className="w-full h-64 md:h-80 lg:h-96 object-cover"
            />
          </figure>
        )}
        
        {/* Article body with drop cap - IBM Plex Sans body text */}
        <div className="article-content max-w-prose">
          {firstParagraph && (
            <p className="text-base leading-relaxed font-body mb-6 text-neutral-700 dark:text-neutral-300">
              <span 
                className="float-left text-6xl md:text-7xl font-serif font-bold leading-none mr-4 mt-2"
              >
                {firstLetter}
              </span>
              <span>
                {renderHighlightedText(restOfFirstParagraph, 1)}
              </span>
            </p>
          )}
          
          {/* Rest of content with highlighting */}
          <HighlightedArticleContent 
            htmlContent={restContent} 
            currentWordRange={currentWordRange}
            articleText={articleText}
          />
          
          {/* Insight links section - extracted from article content */}
          <InsightLinksSection content={content} />
        </div>
      </div>
  );
}

// Helper to extract text segments from HTML with their offsets
interface TextSegment {
  text: string;
  startOffset: number;
  endOffset: number;
}

function extractTextSegments(html: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cumulativeOffset = 0;
  
  // Split HTML into text content segments (content between tags)
  const textParts = html.split(/<[^>]+>/);
  
  for (const part of textParts) {
    if (part.trim()) {
      // Decode HTML entities
      const decoded = part
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'");
      
      const normalizedText = decoded.replace(/\s+/g, ' ').trim();
      if (normalizedText) {
        segments.push({
          text: normalizedText,
          startOffset: cumulativeOffset,
          endOffset: cumulativeOffset + normalizedText.length
        });
        cumulativeOffset += normalizedText.length + 1; // +1 for space between segments
      }
    }
  }
  
  return segments;
}

// Find which segment contains the current word position and the word's local offset
function findWordPosition(segments: TextSegment[], globalStart: number, globalEnd: number): { segmentIndex: number; localStart: number; localEnd: number } | null {
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (globalStart >= seg.startOffset && globalEnd <= seg.endOffset) {
      return {
        segmentIndex: i,
        localStart: globalStart - seg.startOffset,
        localEnd: globalEnd - seg.startOffset
      };
    }
    // Word might span adjustment due to normalization
    if (globalStart >= seg.startOffset && globalStart < seg.endOffset) {
      return {
        segmentIndex: i,
        localStart: globalStart - seg.startOffset,
        localEnd: Math.min(globalEnd - seg.startOffset, seg.text.length)
      };
    }
  }
  return null;
}

// Component to render HTML content with word highlighting during audio playback
function HighlightedArticleContent({ 
  htmlContent, 
  currentWordRange, 
  articleText 
}: { 
  htmlContent: string; 
  currentWordRange: WordRange | null;
  articleText: string;
}) {
  const proseClasses = `prose prose-neutral dark:prose-invert max-w-none font-body
    prose-p:text-base prose-p:leading-relaxed prose-p:mb-6 prose-p:text-neutral-700 dark:prose-p:text-neutral-300
    prose-headings:font-serif prose-headings:font-bold prose-headings:tracking-tight prose-headings:uppercase
    prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:mt-12 prose-h2:mb-5
    prose-h3:text-xl prose-h3:md:text-2xl prose-h3:mt-10 prose-h3:mb-4
    prose-a:text-red-600 prose-a:no-underline hover:prose-a:underline prose-a:font-medium
    prose-blockquote:border-l-4 prose-blockquote:border-red-600 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-neutral-600
    prose-strong:text-neutral-900 dark:prose-strong:text-white
    prose-img:w-full prose-img:rounded-none prose-img:my-8`;

  // If no word is being highlighted, render HTML directly
  if (!currentWordRange || !articleText) {
    return (
      <div 
        className={proseClasses}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }
  
  // Get the current word being spoken
  const currentWord = articleText.slice(currentWordRange.start, currentWordRange.end);
  
  // For accurate highlighting, we need to find the exact occurrence based on character position
  // Count occurrences of this word before the current position to find which instance to highlight
  const textBeforeWord = articleText.slice(0, currentWordRange.start);
  const wordPattern = new RegExp(`\\b${currentWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
  const matchesBefore = (textBeforeWord.match(wordPattern) || []).length;
  
  // Now find and highlight the nth occurrence (matchesBefore + 1) in the HTML
  const escapedWord = currentWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Match word only outside of HTML tags
  const wordRegex = new RegExp(`(>[^<]*?)(\\b${escapedWord}\\b)([^<]*?<)`, 'gi');
  
  let occurrenceCount = 0;
  const targetOccurrence = matchesBefore;
  
  const highlightedHtml = htmlContent.replace(wordRegex, (match, before, word, after) => {
    if (occurrenceCount === targetOccurrence) {
      occurrenceCount++;
      return `${before}<span class="bg-yellow-300 dark:bg-yellow-500/80 text-neutral-900 px-1 py-0.5 rounded" data-testid="highlighted-word">${word}</span>${after}`;
    }
    occurrenceCount++;
    return match;
  });
  
  return (
    <div 
      className={proseClasses}
      dangerouslySetInnerHTML={{ __html: highlightedHtml }}
    />
  );
}
